import Component from '@glimmer/component';
import b64EncodeUnicode from 'cio/utils/base64-unicode';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import Ember from 'ember';
import { inject as service } from '@ember/service';
import AlertsService from 'cio/services/alerts';
import { schedule } from '@ember/runloop';
import { unixNowSeconds } from 'cio/utils/time';
import { GistAuthenticationToken } from 'cio/utils/in-app/credentials';
import { dropTask } from 'ember-concurrency';
import { SearchTemplateItem } from 'cio/app-models/search-template-item';
import ApiService from 'cio/services/api';
import ApiErrorsService from 'cio/services/api-errors';
import AuthService from 'cio/services/auth';
import EnvironmentService from 'cio/services/environment';
import { ANON_ID_KEY } from 'cio/services/analytics';

const DEFAULT_HEIGHT = 1300;

type Location = {
  pathname: string;
  search: string;
  hash: string;
};

export type MessagePayload = {
  width?: number;
  height?: number;
  type?: 'success' | 'error';
  message?: string;
  messageId?: string;
  location?: Location;
  assetInputId?: string;
};

type FlyMessagePayload = {
  userId?: string;
  workspaceId?: string;
  messageId?: string;
  usage?: SearchTemplateItem[];
  assetPath?: string;
  assetInputId?: string;
};

/* eslint-disable spellcheck/spell-checker */
enum SupportedMethods {
  UserLoggedIn = 'userLoggedIn',
  UserLoggedOut = 'userLoggedOut',
  Notification = 'notification',
  LocationChanged = 'locationChanged',
  ShowDeleteMessageModal = 'showDeleteMessageModal',
  ShowAssetLibraryModal = 'showAssetLibraryModal',
  CompletedLoading = 'completedLoading',
}

export enum SupportedFlyMethods {
  SaveFlyUserId = 'saveFlyUserId',
  SaveFlyWorkspaceId = 'saveFlyWorkspaceId',
  InAppMessageUsage = 'inAppMessageUsage',
  DeleteMessageConfirmed = 'deleteMessageConfirmed',
  DeleteMessageCancelled = 'deleteMessageCancelled',
  OnSelectAsset = 'onSelectAsset',
}

/* eslint-enable spellcheck/spell-checker */
const ALL_SUPPORTED_METHODS = Object.values(SupportedMethods);
const isMethodSupported = (m: SupportedMethods) => ALL_SUPPORTED_METHODS.includes(m);

type GistIframeMessage = {
  gistApp?: {
    method: SupportedMethods;
    payload: MessagePayload;
  };
};

type FlyIframeMessage = {
  fly?: {
    method: SupportedFlyMethods;
    payload: FlyMessagePayload;
  };
};

export interface GistEditorArgs {
  loadEditor: boolean;
  authToken: GistAuthenticationToken;
  userId: string;
  onMessageIdChange?: (messageId: string) => unknown;
  onIframeDidLoad?: () => unknown;
}
export default abstract class GistEditor<T extends GistEditorArgs = GistEditorArgs> extends Component<T> {
  @service alerts!: AlertsService;
  @service api!: ApiService;
  @service apiErrors!: ApiErrorsService;
  @service auth!: AuthService;
  @service environment!: EnvironmentService;

  @tracked isLoadingEditor = false;

  @tracked isShowingDeleteMessageModal = false;
  @tracked messageName = '';
  @tracked actualMessageId = '';
  @tracked isUserLoggedIn = false;
  @tracked iframeHeight = DEFAULT_HEIGHT;
  @tracked randomTimestamp = unixNowSeconds();
  @tracked isShowingAssetLibraryModal = false;
  @tracked assetInputId?: string;

  abstract get editorPath(): string;

  get userId() {
    return this.auth?.user?.id ?? ANON_ID_KEY;
  }

  get currentEnvironmentId() {
    return this.environment.current.id ?? '';
  }

  get editorUrl() {
    let { authToken } = this.args;
    if (authToken?.endpoint == null || authToken.payload == null) {
      return '';
    }
    let { endpoint } = authToken;
    let encodedToken = b64EncodeUnicode(JSON.stringify(authToken.payload));
    return `${endpoint}${this.editorPath}?token=${encodedToken}&randomTimestamp=${this.randomTimestamp}`;
  }

  get iframeStyle() {
    let style = `height: ${this.iframeHeight}px;`;
    if (!this.isUserLoggedIn) {
      style += 'display: none !important;';
    }
    return style;
  }

  @action
  startLoadingEditor() {
    this.isLoadingEditor = true;
  }

  @action
  didLoadEditor() {
    this.isLoadingEditor = false;
    if (!Ember.testing) {
      this.postFlyUserId(this.userId);
      this.postFlyWorkspaceId(this.currentEnvironmentId);
    }
    if (this.args.onIframeDidLoad != null) {
      this.args.onIframeDidLoad();
    }
  }

  @action
  watchGistEvents() {
    window.addEventListener('message', this.handleGistIframeMessage);
  }

  @action
  handleGistIframeMessage(event: { data: GistIframeMessage; origin: string; stopImmediatePropagation: () => unknown }) {
    schedule('actions', this, () => {
      if (event.data?.gistApp == null || (!Ember.testing && !this.editorUrl.startsWith(event.origin))) {
        return;
      }
      let { method, payload } = event.data.gistApp;
      if (isMethodSupported(method)) {
        let handler = this[method];
        handler(payload);
      }
    });
  }

  willDestroy() {
    super.willDestroy();
    window.removeEventListener('message', this.handleGistIframeMessage);
  }

  @action
  completedLoading() {
    if (!this.isLoadingEditor) {
      this.postFlyUserId(this.userId);
      this.postFlyWorkspaceId(this.currentEnvironmentId);
    }
  }

  @action
  locationChanged(payload: MessagePayload) {
    if (payload.location == null || this.args.onMessageIdChange == null) {
      return;
    }

    let messageId = this.getMessageIdFromLocation(payload.location);
    this.args.onMessageIdChange(messageId);

    if (messageId.length > 0) {
      this.postInAppMessageUsage.perform(messageId);
    }
  }

  @action
  showDeleteMessageModal(payload: MessagePayload) {
    let { message } = payload;
    if (message == null) {
      return;
    }
    this.messageName = message;
    this.isShowingDeleteMessageModal = true;
  }

  @action
  showAssetLibraryModal(payload: MessagePayload) {
    if (payload.assetInputId == null) {
      return;
    }

    this.assetInputId = payload.assetInputId;
    this.isShowingAssetLibraryModal = true;
  }

  @action
  userLoggedIn() {
    this.isUserLoggedIn = true;
  }

  @action
  userLoggedOut() {
    // force editorUrl update to reload iframe
    this.randomTimestamp = unixNowSeconds();
  }

  @action
  notification(payload: MessagePayload) {
    let { type, message } = payload;
    if (message == null) {
      return;
    }
    if (type === 'success') {
      this.alerts.success('gist.app', message);
    } else {
      this.alerts.error('gist.app', message);
    }
  }

  postInAppMessageUsage = dropTask(async (messageId: string) => {
    let response: SearchTemplateItem[] = [];
    try {
      response = await this.api.request(`in_app/${messageId}/usage`, {
        ignoreErrors: true,
      });
    } catch (e) {
      this.apiErrors.report(e);
    }

    this.postMessageToGist({
      fly: {
        method: SupportedFlyMethods.InAppMessageUsage,
        payload: {
          usage: response,
        },
      },
    });

    return response;
  });

  // This method posts the userId to Gist to enable a better Mixpanel integration
  @action
  postFlyUserId(userId: string) {
    this.postMessageToGist({
      fly: {
        method: SupportedFlyMethods.SaveFlyUserId,
        payload: {
          userId,
        },
      },
    });
  }

  @action
  postFlyWorkspaceId(workspaceId: string) {
    this.postMessageToGist({
      fly: {
        method: SupportedFlyMethods.SaveFlyWorkspaceId,
        payload: {
          workspaceId,
        },
      },
    });
  }

  private getMessageIdFromLocation(location: Location) {
    let { pathname } = location;
    if (pathname == null) {
      return '';
    }
    let matches = pathname.match(/\/message\/(.*)/);
    if (matches == null || matches.length < 2) {
      return '';
    }
    return matches[1];
  }

  protected postMessageToGist(message: FlyIframeMessage) {
    let gistFrame = document.getElementById('gist-editor') as HTMLIFrameElement;
    if (gistFrame == null) {
      return;
    }
    gistFrame.contentWindow?.postMessage(message, '*');
  }
}
